import {
  DataGridPro,
  getGridDateOperators,
  getGridNumericOperators,
  getGridStringOperators,
  GridToolbar,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import React, { useEffect, useState } from 'react';
import { GlobalVariables } from '../../../globalVar';
import customAxios from '../../../utils/customAxios';
import { getToast } from '../../../utils/toastConfig';
import { useTableLocale } from '../../../hooks/useTableLocale';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';

const LOCAL_COLUMN_WIDTH = GlobalVariables.getDefaultColumnWidth();
const INITIAL_PAGINATION = {
  page: 0,
  pageSize: 25,
};

const EquipmentsIframeTable = () => {
  const apiRef = useGridApiRef();
  const [isFetching, setIsFetching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [equipments, setEquipments] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [filterModel, setFilterModel] = useState({
    items: [],
    logicOperator: 'and',
    quickFilterValues: [],
    quickFilterLogicOperator: 'and',
  });
  const [paginationModel, setPaginationModel] = useState(INITIAL_PAGINATION);

  const [showExportModal, setShowExportModal] = useState(false);

  const fetchEquipments = async (req, res) => {
    setIsFetching(true);
    const endpoint = `${GlobalVariables.getURL()}import/equipments`;

    try {
      const response = await axios.post(endpoint, {
        pagination: {
          rowsPerPage: paginationModel.pageSize,
          page: paginationModel.page + 1,
        },
      });

      if (response.status === 204) {
        setEquipments([]);
        setRowCount(0);
        return;
      }

      setEquipments(response.data.data.rows);
      setRowCount(response.data.data.totalRows);
    } catch (error) {
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchEquipments();
  }, []);

  useEffect(() => {
    if (apiRef.current) {
      apiRef.current.setDensity('compact');
    }
  }, [apiRef]);

  const locale = useTableLocale();

  const initialColumns = [
    {
      field: 'id',
      headerName: 'ID',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'submission',
      headerName: 'Submission',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'ticket_sigma',
      headerName: 'Ticket',
      width: LOCAL_COLUMN_WIDTH,
      filterable: true,
      filterOperators: getGridNumericOperators().filter(
        (operator) => operator.value !== 'isAnyOf'
      ),
    },
    {
      field: 'ticket_oxxo',
      headerName: 'Ticket',
      width: LOCAL_COLUMN_WIDTH,
      filterable: true,
      type: 'string',
      filterOperators: [
        ...getGridStringOperators()
          .filter((o) => o.value !== 'isAnyOf')
          .map((o) => {
            return {
              ...o,
              InputComponent: ({ item, applyValue }) => {
                const [value, setValue] = useState(
                  item.value ? item.value : ''
                );

                return (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <TextField
                      label='Valor'
                      placeholder='Valor del filtro'
                      variant='standard'
                      value={value}
                      onChange={(e) => {
                        setValue(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          applyValue({ ...item, value: e.target.value });
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                    <p style={{ fontWeight: 'bold', fontSize: 14 }}>
                      Dar "Enter" para buscar
                    </p>
                  </div>
                );
              },
            };
          }),
        {
          value: 'not',
          InputComponent: ({ item, applyValue }) => {
            const [value, setValue] = useState(item.value ? item.value : '');

            return (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <TextField
                  label='Valor'
                  placeholder='Valor del filtro'
                  variant='standard'
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      applyValue({ ...item, value: e.target.value });
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                <p style={{ fontWeight: 'bold', fontSize: 14 }}>
                  Dar "Enter" para buscar
                </p>
              </div>
            );
          },
        },
      ],
    },
    {
      field: 'os_num',
      headerName: 'Número de OS',
      width: LOCAL_COLUMN_WIDTH,
      filterable: true,
      type: 'string',
      filterOperators: [
        ...getGridStringOperators()
          .filter((o) => o.value !== 'isAnyOf')
          .map((o) => {
            return {
              ...o,
              InputComponent: ({ item, applyValue }) => {
                const [value, setValue] = useState(
                  item.value ? item.value : ''
                );

                return (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <TextField
                      label='Valor'
                      placeholder='Valor del filtro'
                      variant='standard'
                      value={value}
                      onChange={(e) => {
                        setValue(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          applyValue({ ...item, value: e.target.value });
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                    <p style={{ fontWeight: 'bold', fontSize: 14 }}>
                      Dar "Enter" para buscar
                    </p>
                  </div>
                );
              },
            };
          }),
        {
          value: 'not',
          InputComponent: ({ item, applyValue }) => {
            const [value, setValue] = useState(item.value ? item.value : '');

            return (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <TextField
                  label='Valor'
                  placeholder='Valor del filtro'
                  variant='standard'
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      applyValue({ ...item, value: e.target.value });
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                <p style={{ fontWeight: 'bold', fontSize: 14 }}>
                  Dar "Enter" para buscar
                </p>
              </div>
            );
          },
        },
      ],
    },
    {
      field: 'empod',
      headerName: 'Plaza',
      width: LOCAL_COLUMN_WIDTH,
      filterable: true,
      type: 'string',
      filterOperators: [
        ...getGridStringOperators()
          .filter((o) => o.value !== 'isAnyOf')
          .map((o) => {
            return {
              ...o,
              InputComponent: ({ item, applyValue }) => {
                const [value, setValue] = useState(
                  item.value ? item.value : ''
                );

                return (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <TextField
                      label='Valor'
                      placeholder='Valor del filtro'
                      variant='standard'
                      value={value}
                      onChange={(e) => {
                        setValue(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          applyValue({ ...item, value: e.target.value });
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                    <p style={{ fontWeight: 'bold', fontSize: 14 }}>
                      Dar "Enter" para buscar
                    </p>
                  </div>
                );
              },
            };
          }),
        {
          value: 'not',
          InputComponent: ({ item, applyValue }) => {
            const [value, setValue] = useState(item.value ? item.value : '');

            return (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <TextField
                  label='Valor'
                  placeholder='Valor del filtro'
                  variant='standard'
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      applyValue({ ...item, value: e.target.value });
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                <p style={{ fontWeight: 'bold', fontSize: 14 }}>
                  Dar "Enter" para buscar
                </p>
              </div>
            );
          },
        },
      ],
    },
    {
      field: 'site',
      headerName: 'Sucursal',
      width: LOCAL_COLUMN_WIDTH,
      filterable: true,
      type: 'string',
      filterOperators: [
        ...getGridStringOperators()
          .filter((o) => o.value !== 'isAnyOf')
          .map((o) => {
            return {
              ...o,
              InputComponent: ({ item, applyValue }) => {
                const [value, setValue] = useState(
                  item.value ? item.value : ''
                );

                return (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <TextField
                      label='Valor'
                      placeholder='Valor del filtro'
                      variant='standard'
                      value={value}
                      onChange={(e) => {
                        setValue(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          applyValue({ ...item, value: e.target.value });
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                    <p style={{ fontWeight: 'bold', fontSize: 14 }}>
                      Dar "Enter" para buscar
                    </p>
                  </div>
                );
              },
            };
          }),
        {
          value: 'not',
          InputComponent: ({ item, applyValue }) => {
            const [value, setValue] = useState(item.value ? item.value : '');

            return (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <TextField
                  label='Valor'
                  placeholder='Valor del filtro'
                  variant='standard'
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      applyValue({ ...item, value: e.target.value });
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                <p style={{ fontWeight: 'bold', fontSize: 14 }}>
                  Dar "Enter" para buscar
                </p>
              </div>
            );
          },
        },
      ],
    },
    {
      field: 'cr',
      headerName: 'CR',
      width: LOCAL_COLUMN_WIDTH,
      filterable: true,
      type: 'string',
      filterOperators: [
        ...getGridStringOperators()
          .filter((o) => o.value !== 'isAnyOf')
          .map((o) => {
            return {
              ...o,
              InputComponent: ({ item, applyValue }) => {
                const [value, setValue] = useState(
                  item.value ? item.value : ''
                );

                return (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <TextField
                      label='Valor'
                      placeholder='Valor del filtro'
                      variant='standard'
                      value={value}
                      onChange={(e) => {
                        setValue(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          applyValue({ ...item, value: e.target.value });
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                    <p style={{ fontWeight: 'bold', fontSize: 14 }}>
                      Dar "Enter" para buscar
                    </p>
                  </div>
                );
              },
            };
          }),
        {
          value: 'not',
          InputComponent: ({ item, applyValue }) => {
            const [value, setValue] = useState(item.value ? item.value : '');

            return (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <TextField
                  label='Valor'
                  placeholder='Valor del filtro'
                  variant='standard'
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      applyValue({ ...item, value: e.target.value });
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                <p style={{ fontWeight: 'bold', fontSize: 14 }}>
                  Dar "Enter" para buscar
                </p>
              </div>
            );
          },
        },
      ],
    },
    {
      field: 'location',
      headerName: 'Ubicación',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'technical',
      headerName: 'Técnico asignado',
      width: LOCAL_COLUMN_WIDTH,
      filterable: true,
      type: 'string',
      filterOperators: [
        ...getGridStringOperators()
          .filter((o) => o.value !== 'isAnyOf')
          .map((o) => {
            return {
              ...o,
              InputComponent: ({ item, applyValue }) => {
                const [value, setValue] = useState(
                  item.value ? item.value : ''
                );

                return (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <TextField
                      label='Valor'
                      placeholder='Valor del filtro'
                      variant='standard'
                      value={value}
                      onChange={(e) => {
                        setValue(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          applyValue({ ...item, value: e.target.value });
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                    <p style={{ fontWeight: 'bold', fontSize: 14 }}>
                      Dar "Enter" para buscar
                    </p>
                  </div>
                );
              },
            };
          }),
        {
          value: 'not',
          InputComponent: ({ item, applyValue }) => {
            const [value, setValue] = useState(item.value ? item.value : '');

            return (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <TextField
                  label='Valor'
                  placeholder='Valor del filtro'
                  variant='standard'
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      applyValue({ ...item, value: e.target.value });
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                <p style={{ fontWeight: 'bold', fontSize: 14 }}>
                  Dar "Enter" para buscar
                </p>
              </div>
            );
          },
        },
      ],
    },
    {
      field: 'equipment_type',
      headerName: 'Tipo de equipo',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'serial_num',
      headerName: '# serie',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'asset_num',
      headerName: '# equipo',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'brand',
      headerName: 'Marca',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'model',
      headerName: 'Modelo / Tipo rack',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'year',
      headerName: 'Año',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'cause',
      headerName: 'Causa del movimiento',
      width: LOCAL_COLUMN_WIDTH,
      filterable: true,
      type: 'singleSelect',
      valueOptions: [
        'Daño',
        'Garantía',
        'Otro',
        'Rentec',
        'Sucursal en remodelación',
        'Sucursal nueva',
        'Mantenimiento Preventivo',
        'N/A',
      ],
    },
    {
      field: 'tm',
      headerName: 'Tipo de movimiento',
      width: LOCAL_COLUMN_WIDTH,
      filterable: true,
      type: 'singleSelect',
      valueOptions: [
        'Instalación',
        'Retiro',
        'Mantenimiento Preventivo',
        'N/A',
      ],
    },
    {
      field: 'service_date',
      headerName: 'Fecha y hora del servicio',
      width: LOCAL_COLUMN_WIDTH,
      filterable: true,
      type: 'date',
      valueGetter: ({ value }) => {
        return moment(value, 'DD/MM/YYYY HH:mm').toDate();
      },
      valueFormatter: ({ value }) => {
        return moment(value).format('DD/MM/YYYY HH:mm');
      },
      filterOperators: [
        ...getGridDateOperators(),
        {
          value: 'between',
          label: 'entre',
          InputComponent: ({ item, applyValue }) => {
            const [dateRanges, setDateRanges] = useState(
              item.value
                ? item.value.map((date) => moment(date).format('YYYY-MM-DD'))
                : [null, null]
            );

            const handleRangeChange = (newValue) => {
              const formattedRange = newValue.map((date) =>
                date ? moment(date).format('YYYY-MM-DD') : null
              );

              setDateRanges(formattedRange);
              applyValue({
                ...item,
                value: formattedRange,
              });
            };

            return (
              <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale={moment.locale()}
              >
                <DateRangePicker
                  label='Seleccionar rango de fechas'
                  value={dateRanges.map((date) => (date ? moment(date) : null))}
                  onChange={handleRangeChange}
                  localeText={{ start: 'Inicio', end: 'Fin' }}
                  slotProps={{ textField: { variant: 'standard' } }}
                />
              </LocalizationProvider>
            );
          },
        },
      ],
    },
    {
      field: 'start_job_date',
      headerName: 'Fecha de inicio de trabajo',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
    {
      field: 'end_job_date',
      headerName: 'Fecha de fin de trabajo',
      width: LOCAL_COLUMN_WIDTH,
      filterable: false,
    },
  ];

  const handleFilterModelChange = async (filterModel) => {
    setFilterModel(filterModel);
    setPaginationModel(INITIAL_PAGINATION);
    setIsFetching(true);

    try {
      const endpoint = `${GlobalVariables.getURL()}import/equipments`;
      const response = await customAxios.post(endpoint, {
        filters: filterModel,
        pagination: {
          rowsPerPage: INITIAL_PAGINATION.pageSize,
          page: INITIAL_PAGINATION.page + 1,
        },
      });

      if (response.status === 204) {
        setEquipments([]);
        setRowCount(0);
        return;
      }

      setEquipments(response.data.data.rows);
      setRowCount(response.data.data.totalRows);
    } catch (error) {
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    } finally {
      setIsFetching(false);
    }
  };

  const handlePageModelChange = async (paginationModel) => {
    setPaginationModel(paginationModel);
    setIsFetching(true);

    try {
      const endpoint = `${GlobalVariables.getURL()}import/equipments`;
      const response = await customAxios.post(endpoint, {
        filters: filterModel,
        pagination: {
          rowsPerPage: paginationModel.pageSize,
          page: paginationModel.page + 1,
        },
      });

      if (response.status === 204) {
        setEquipments([]);
        setRowCount(0);
        return;
      }

      setEquipments(response.data.data.rows);
      setRowCount(response.data.data.totalRows);
    } catch (error) {
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    } finally {
      setIsFetching(false);
    }
  };

  const handleExportClick = async (e) => {
    e.preventDefault();

    if (isLoading) return;

    setIsLoading(true);

    try {
      const endpoint = `${GlobalVariables.getURL()}import/equipments/export`;
      const response = await axios.post(
        endpoint,
        { filters: filterModel },
        { responseType: 'arraybuffer' }
      );

      if (response.status === 200) {
        getToast({
          status: 'success',
          title: 'Proceso iniciado, favor de revisar su correo en unos minutos',
          duration: 5,
        });

        // Create a blob for the response data
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        // Create a temporary URL for the Blob
        const url = window.URL.createObjectURL(blob);

        // Create a temporary anchor element
        const link = document.createElement('a');
        link.href = url;
        link.download = `export_mtto_equipos_${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`;

        // Append the link to the document body and trigger the click
        document.body.appendChild(link);
        link.click();

        // Clean up: remove the link and revoke the temporary URL
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.log(error);
      getToast({ status: 'error', title: error.response.data.message });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='table-module-container'>
      <div className='datatable'>
        <DataGridPro
          apiRef={apiRef}
          localeText={locale.components.MuiDataGrid.defaultProps.localeText}
          loading={isFetching}
          rows={equipments}
          rowCount={rowCount}
          columns={initialColumns}
          filterMode='server'
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          pagination
          paginationMode='server'
          paginationModel={paginationModel}
          pageSizeOptions={[25, 50, 100, 1000]}
          onPaginationModelChange={handlePageModelChange}
          initialState={{
            sorting: {
              sortModel: [{ field: 'service_date', sort: 'desc' }],
            },
            columns: {
              columnVisibilityModel: {
                id: false,
                submission: false,
              },
            },
            pinnedColumns: { right: ['action'] },
          }}
          slots={{
            noRowsOverlay: () => (
              <Stack height='100%' alignItems='center' justifyContent='center'>
                No hay datos en la base de datos
              </Stack>
            ),
            toolbar: () => (
              <GridToolbarContainer>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <GridToolbarFilterButton />
                    <Button
                      onClick={handleExportClick}
                      variant='outlined'
                      disabled={
                        (filterModel.items.length === 0 &&
                          filterModel.quickFilterValues.length === 0) ||
                        isLoading
                      }
                    >
                      {isLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size='1rem' />
                          <Typography>Exportando...</Typography>
                        </Box>
                      ) : (
                        <Typography>Exportar</Typography>
                      )}
                    </Button>
                    <Typography fontSize={12}>
                      Selecciona un filtro para exportar
                    </Typography>
                  </Box>
                  <Box>
                    <GridToolbarQuickFilter />
                  </Box>
                </Box>
              </GridToolbarContainer>
            ),
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
        />
      </div>
    </div>
  );
};

export default EquipmentsIframeTable;
